import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import s from './SequenceVersionDetail.module.scss';
import { FormattedMessage, injectIntl } from 'react-intl';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { initialSequenceVersionState } from '../../reducers/initialState';
import { v4 as uuidv4 } from 'uuid';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Banner from '../Banner/Banner';
import { Link, useParams, useNavigate, Navigate } from 'react-router-dom';
import SequenceVersionFiles from './SequenceVersionFiles';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import classNames from 'classnames';
import { v4 as uuid } from 'uuid';
import Cookies from 'js-cookie';
import { isRequired, compareValues, getQuerystring, getModelCategoryPermission } from '../../utils';
import { WidgetVisibility, IsWidgetAccessible } from '../../utils/widgetManager';
import Dropdown from '../WSAControls/DroprdownContainer/Dropdown';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import ModelConfigurationContentTemplate from '../ModelTab/ModelConfigurationContentTemplate';
import Breadcrumb from '../WSAControls/Breadcrumb/BreadCrumb';
import { ModelCategoryPermission } from '../../constants';
import SequenceFileDropzone from './SequenceFileDropzone';



const SequenceVersionDetail = ({
  userProfileWidgets,
  currentTimezone,
  flowSequences,
  sequenceVersionManager = { ...initialSequenceVersionState.sequenceVersionManager },
  saveSequenceVersion,
  describeSequenceVersion,
  changeSequenceVersion,
  userModelCategories,
  intl
}) => {
  let IsEdit = false;
  let IsView = false;
  const envName = process.env.REACT_APP_ENV_NAME_SHORT;
  let { ModelCategoryId, ModelId, sequenceVersionId } = useParams();
  let IsNew = getQuerystring('new') || '0';
  const history = useNavigate();
  const backLink = `/operational-setup/model-category-configuration/${ModelCategoryId}/model-configuration/${ModelId}/sequence-version`;

  const [formError, setFormError] = useState({});
  const [active, setActive] = useState(false);
  const [deleteSequenceVersionFileId, setDeleteSequenceVersionFileId] = useState(0);


  sequenceVersionId = parseInt(sequenceVersionId);


  const dialogDelete = intl.formatMessage({
    id: 'sequenceVersionManagement.confirmDelete',
    defaultMessage: 'Are you sure you want to delete this sequence file?'
  });

  let isPublished = (sequenceVersionManager.selectedSequenceVersion && sequenceVersionManager.selectedSequenceVersion.IsPublished) || false;
  IsEdit = sequenceVersionId !== 0 && !isPublished;
  IsView = sequenceVersionId !== 0 && isPublished;

  const changeAllowed = IsWidgetAccessible(userProfileWidgets, `SEQUENCEVERSIONUPDATE`);
  const deleteAllowed = IsWidgetAccessible(userProfileWidgets, `SEQUENCEFILESDELETE`);

  let messageId = (sequenceVersionManager && sequenceVersionManager.displayMessageCode) || 'none';
  const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });
  let selectedFileValidateCallbackFn = null;
  let fileUploadStatusCallbackFn = null;


  const sequenceVersionName =
    (sequenceVersionManager.selectedSequenceVersion ? sequenceVersionManager.selectedSequenceVersion.Name : '') || '';

  let heading = '';
  if (IsEdit) {
    heading = intl.formatMessage({ id: 'sequenceVersionManagement.editModelSequenceVersion', defaultMessage: 'Edit Model Sequence Version' })
  } else if (IsView) {
    heading = intl.formatMessage({ id: 'sequenceVersionManagement.viewModelSequenceVersion', defaultMessage: 'View Model Sequence Version' })
  } else {
    heading =
      intl.formatMessage({ id: 'sequenceVersionManagement.addModelSequenceVersion', defaultMessage: 'Add Model Sequence Version' })
  }

  //Checking permission and redirecting to unauth page if unauthorised
  if (IsEdit && !IsWidgetAccessible(userProfileWidgets, 'SEQUENCEVERSIONUPDATE')) {
    history('/unauth');
  }
  if (IsView && !IsWidgetAccessible(userProfileWidgets, 'SEQUENCEVERSIONUPDATE')) {
    history('/unauth');
  }
  if (sequenceVersionId === 0 && !IsWidgetAccessible(userProfileWidgets, 'SEQUENCEVERSIONADD')) {
    history('/unauth');
  }

  let widgetCode = '';

  if (sequenceVersionId) {
    widgetCode = 'SEQUENCEVERSIONUPDATE';
  } else {
    widgetCode = 'SEQUENCEVERSIONADD';
  }

  useEffect(() => {
    changeSequenceVersion(initialSequenceVersionState.sequenceVersionManager);
    if (sequenceVersionId) describeSequenceVersion(ModelCategoryId, sequenceVersionId, widgetCode);
  }, [describeSequenceVersion, ModelCategoryId, sequenceVersionId, changeSequenceVersion, widgetCode]);


  //Saving
  const submitForm = e => {
    e.preventDefault();

    const validation = validateInput();
    if (!validation.isValid) {
      return;
    }


    let saveData = sequenceVersionManager.selectedSequenceVersion;
    saveData.ModelId = ModelId;
    saveData.IsEdit = IsEdit;
    saveData.UpdatedBy = Cookies.get(`userid-${envName}`) || 1;
    saveData.ModelCategoryId = ModelCategoryId;

    if (!IsEdit) {
      saveData.SequenceVersionId = 0;
      saveData.GuidIdentifier = uuidv4();
      saveData.ModelId = ModelId;
    }

    saveSequenceVersion(saveData, widgetCode, validation.filesToUpload);
  };

  const validateInput = () => {
    let formError = {};

    let isValid = true;
    let preparedFilesToUpload = []

    if (
      !sequenceVersionManager ||
      !sequenceVersionManager.selectedSequenceVersion ||
      !sequenceVersionManager.selectedSequenceVersion.Name ||
      isRequired(sequenceVersionManager.selectedSequenceVersion.Name, 1)
    ) {
      formError.Name = intl.formatMessage({
        id: 'sequenceVersionManagement.nameMandatory',
        defaultMessage: 'Version name is a mandatory field'
      });
      isValid = false;
    }

    if (
      !sequenceVersionManager ||
      !sequenceVersionManager.selectedSequenceVersion ||
      !sequenceVersionManager.selectedSequenceVersion.VersionNo ||
      isRequired(sequenceVersionManager.selectedSequenceVersion.VersionNo, 1)
    ) {
      formError.VersionNo = intl.formatMessage({
        id: 'sequenceVersionManagement.versionNoMandatory',
        defaultMessage: 'Version number is a mandatory field'
      });
      isValid = false;
    } else {
      if (!validateVersionNo(sequenceVersionManager.selectedSequenceVersion.VersionNo)) {
        formError.VersionNo = intl.formatMessage({
          id: 'sequenceVersionManagement.versionNoFomatError',
          defaultMessage: 'Version number format should be 1.0.0'
        });
        isValid = false;
      }
    }

    if (
      !sequenceVersionManager ||
      !sequenceVersionManager.selectedSequenceVersion ||
      !sequenceVersionManager.selectedSequenceVersion.VerifyErrorCode ||
      isRequired(sequenceVersionManager.selectedSequenceVersion.VerifyErrorCode, 1)
    ) {
      formError.VerifyErrorCode = intl.formatMessage({
        id: 'sequenceVersionManagement.verifyErrorCodeMandatory',
        defaultMessage: 'Error code is a mandatory field'
      });
      isValid = false;
    }

    if (
      !sequenceVersionManager ||
      !sequenceVersionManager.selectedSequenceVersion ||
      !sequenceVersionManager.selectedSequenceVersion.VerifyWarningCode ||
      isRequired(sequenceVersionManager.selectedSequenceVersion.VerifyWarningCode, 1)
    ) {
      formError.VerifyWarningCode = intl.formatMessage({
        id: 'sequenceVersionManagement.verifyErrorCodeMandatory',
        defaultMessage: 'Warning code is a mandatory field'
      });
      isValid = false;
    }
    if (
      !sequenceVersionManager ||
      !sequenceVersionManager.selectedSequenceVersion ||
      !sequenceVersionManager.selectedSequenceVersion.StartStateId
    ) {
      formError.StartStateId = intl.formatMessage({
        id: 'sequenceVersionManagement.startStateMandatory',
        defaultMessage: 'Sequence start state is a mandatory field'
      });
      isValid = false;
    }

    if (selectedFileValidateCallbackFn) {
      //checking if the selected files are valid or not
      const validFiles = selectedFileValidateCallbackFn();
      if (validFiles == null) {
        isValid = false;
      }
      if (isValid && validFiles && validFiles.length > 0) {
        let preparedFiles = prepareFilesToBeUploaded(validFiles)
        if (preparedFiles && preparedFiles.length > 0) {
          preparedFilesToUpload = preparedFiles; // Store for return
        } else {
          preparedFilesToUpload = []
        }
      } else {
        preparedFilesToUpload = []
      }
    }

    setFormError(formError);

    return { isValid, filesToUpload: preparedFilesToUpload };
  };

  const prepareFilesToBeUploaded = (files) => {
    let preparedFiles = [];
    if (files && files.length > 0) {
      preparedFiles = files.map(item => {
        let hasSuperState = (item.state_type && item.state_type != "super_state") || item.HasSuperState || false
        let SuperStateId = 0
        if (hasSuperState) {
          if (item.parent_state) {
            let superStateCode = item.parent_state.toUpperCase().replace("STATE", "")
            if (superStateCode) {
              SuperStateId = flowSequences.find(fs => fs.Code.toUpperCase() === superStateCode)?.SetpointId || 0;
            }

          } else if (item.SuperStateId) {
            SuperStateId = item.SuperStateId
          }
        }
        let modifiedItem = {
          Id: item.id,
          SequenceVersionFileId: item.SequenceVersionFileId || 0,
          SequenceStateId: item.SetpointId,
          HasSuperState: hasSuperState,
          SuperStateId: SuperStateId,
          JsonText: JSON.stringify(item.content),
          WaitFor: item.wait_for || item.WaitFor || 0,
          ShowCommand: item.ShowCommand || false,
          IsUploaded: false
        }
        return modifiedItem
      })
    }
    return preparedFiles

  }


  //on control value change
  const onChange = e => {
    if (e.target && e.target.type && e.target.type === 'checkbox') {
      sequenceVersionManager.selectedSequenceVersion = {
        ...sequenceVersionManager.selectedSequenceVersion,
        [e.target.name]: e.target.checked
      };
    } else {
      sequenceVersionManager.selectedSequenceVersion = {
        ...sequenceVersionManager.selectedSequenceVersion,
        [e.target.name]: e.target.value
      };
    }
    changeSequenceVersion(sequenceVersionManager);
  };

  const onSequenceFileRemove = sequenceVersionFileId => {
    setDeleteSequenceVersionFileId(sequenceVersionFileId);
    handleCommand();
  };

  const validateVersionNo = val => {
    const regex = /^\d+(\.\d+)*$/;
    return regex.test(val);
  };
  const onVersionNoChange = e => {
    var regex = /^(([0-9.]?)*)+$/;
    if (e.target.value && regex.test(e.target.value)) {
      onChange(e);
    }
  };

  const addNewbtnClick = () => {
    let newUrl = `/operational-setup/model-category-configuration/${ModelCategoryId}/model-configuration/${ModelId}/sequence-version/${sequenceVersionId}/sequence-version-file/0`;
    history(newUrl);
  };

  const viewOrChangeBtnClick = sequenceVersionFileId => {
    let newUrl = `/operational-setup/model-category-configuration/${ModelCategoryId}/model-configuration/${ModelId}/sequence-version/${sequenceVersionId}/sequence-version-file/${sequenceVersionFileId}${IsView ? '?view=1' : ''
      }`;
    history(newUrl);
  };

  const sequenceSetpointOptions = () => {
    let sequenceSetpointData = [];
    flowSequences.forEach(element => {
      sequenceSetpointData.push({
        ...element,
        label: element.Name,
        value: element.SetpointId
      });
    });

    let sortedData = sequenceSetpointData.sort(compareValues('label'));
    return sortedData;
  };

  const onDropdownChange = e => {
    sequenceVersionManager.selectedSequenceVersion = {
      ...sequenceVersionManager.selectedSequenceVersion,
      StartStateId: e.value
    };
    changeSequenceVersion(sequenceVersionManager);
  };

  const redirect = () => {
    if (sequenceVersionManager.isOpSuccessful && !sequenceVersionManager.showBanner) {
      if (
        sequenceVersionManager &&
        sequenceVersionManager.selectedSequenceVersion &&
        sequenceVersionManager.selectedSequenceVersion.SequenceVersionId
      ) {
        if (sequenceVersionManager.performDelete) {
          sequenceVersionManager.performDelete = false;
          sequenceVersionManager.isOpSuccessful = false;
          return (
            <Navigate
              to={`/operational-setup/model-category-configuration/${ModelCategoryId}/model-configuration/${ModelId}/sequence-version/?id=${sequenceVersionId}`}
            />
          );
        }

        if (!sequenceVersionManager.selectedSequenceVersion.fileUploadStatus) {
          return <Navigate to={backLink} />;
        } else {
          let uploadFailed = isFileUploadFailed()
          if (!uploadFailed) {
            return (
              <Navigate
                to={`/operational-setup/model-category-configuration/${ModelCategoryId}/model-configuration/${ModelId}/sequence-version/?id=${sequenceVersionId}?new=1`}
              />
            );
          }
        }

      } else if (sequenceVersionManager.sequenceVersionId && IsNew == '0') {
        let uploadFailed = isFileUploadFailed()
        if (!uploadFailed) {
          return (
            <Navigate
              to={`/operational-setup/model-category-configuration/${ModelCategoryId}/model-configuration/${ModelId}/sequence-version/${sequenceVersionManager.sequenceVersionId}?new=1`}
            />
          )
        };
      }
    }
  }


const isFileUploadFailed = () => {
  let failed = false
  if (sequenceVersionManager.selectedSequenceVersion.fileUploadStatus) {
    Object.entries(sequenceVersionManager.selectedSequenceVersion.fileUploadStatus).map(([key, value]) => {
      if (!value.status) {
        failed = true
      }
    })
    return failed
  }
}

const handleCommand = () => {
  setActive(true);
};

const onDeleteClick = () => {
  let saveData = sequenceVersionManager.selectedSequenceVersion;
  saveData.IsEdit = IsEdit;
  saveData.ModelCategoryId = ModelCategoryId
  saveData.SequenceFile = {
    SequenceVersionFileId: deleteSequenceVersionFileId,
    IsPerformDelete: true,
    UpdatedBy: Cookies.get(`userid-${envName}`) || 1
  };
  sequenceVersionManager.performDelete = true;
  changeSequenceVersion(sequenceVersionManager);
  saveSequenceVersion(saveData, 'SEQUENCEFILESDELETE');
};

const handleConfirm = () => {
  onDeleteClick();
  handleClose();
};

const handleClose = () => {
  setActive(false);
  setDeleteSequenceVersionFileId(0);
};

const onPublishBtnClick = () => {
  if (!validatePublish()) {
    return;
  }
  let saveData = sequenceVersionManager.selectedSequenceVersion;
  saveData.UpdatedBy = Cookies.get(`userid-${envName}`) || 1;
  saveData.ModelId = ModelId;
  saveData.ModelCategoryId = ModelCategoryId
  saveData.IsPublished = true;
  saveSequenceVersion(saveData, 'SEQUENCEVERSIONPUBLISH');
};

const validatePublish = () => {
  let seqVerFiles =
    (sequenceVersionManager.selectedSequenceVersion && sequenceVersionManager.selectedSequenceVersion.SequenceVersionFiles) || [];
  let predefinedSeqs = flowSequences.filter(item => item.IsPredefined);
  let isValid = true;
  let missingSequences = [];
  predefinedSeqs.forEach(item => {
    let verFile = seqVerFiles.find(file => file.SequenceStateId == item.SetpointId);
    if (verFile == null) {
      isValid = false;
      missingSequences.push(item.Code);
    }
  });
  let formError = {};
  if (!isValid) {
    formError.PublishError =
      intl.formatMessage({
        id: 'sequenceVersionManagement.predefinedSequenceVersionFileMandatory',
        defaultMessage: 'Sequence version file missing for predefined states'
      }) + ` - ${missingSequences.join(',')}`;
  } else {
    formError.PublishError = null;
  }
  setFormError(formError);
  return isValid;
};

//Used by SequenceFileDropZone component to populate sequence version data  from sequence_config.json file
const updateDetailsFromSequenceConfig = (sequenceConfigData) => {
  sequenceVersionManager.selectedSequenceVersion = {
    ...sequenceVersionManager.selectedSequenceVersion,
    ...sequenceConfigData
  };
  changeSequenceVersion(sequenceVersionManager);

}

//Wiring up the selectedFilesValidateCallback
const selectedFilesValidateCallback = (fnValidate) => {
  selectedFileValidateCallbackFn = fnValidate;
}

const fileUploadStatusCallback = (fnfileUploadStatus) => {
  fileUploadStatusCallbackFn = fnfileUploadStatus;
}

let ddlClass = formError && formError.StartStateId ? s.ddlError : '';

const labelText = sequenceVersionName ? `${heading} ${sequenceVersionName} ` : `${heading}`;
const breadcrumbItems = [
  { label: <FormattedMessage id="modelcategory.modelcategory" defaultMessage="Model Category" />, href: '/operational-setup/model-category-list' },
  { label: <FormattedMessage id="model.models" defaultMessage="Models" />, href: `/operational-setup/model-category-configuration/${ModelCategoryId}/model-list` },
  { label: <FormattedMessage id="model.ModelSequences" defaultMessage="Model Sequences" />, href: `/operational-setup/model-category-configuration/${ModelCategoryId}/model-configuration/${ModelId}/sequence-version` },
  { label: labelText } // Last item has no href (current page)
];


let isModelCategoryOwner = getModelCategoryPermission(userModelCategories, ModelCategoryId, ModelCategoryPermission.Owner)


return (
  <div className={s.sequenceVersionDetail}>
    {sequenceVersionManager.isLoading && <LoadingSpinner />}
    {redirect()}
    <ConfirmDialog title={dialogDelete} onConfirm={handleConfirm} onClose={handleClose} showDialog={active} />
    <ModelConfigurationContentTemplate selectedPage="manageModelSequences" userProfileWidgets={userProfileWidgets} intl={intl}>
      <Banner
        key={uuid()}
        failureText={messageText}
        showBanner={sequenceVersionManager.showBanner}
        status={sequenceVersionManager.isOpSuccessful}
        warning={sequenceVersionManager.warning}
        successText={messageText}
      />

      <div className={s.contentWrapper}>

        <div className={s.sequenceVersionManagementHeader}>
          <Row>
            <Col className="col-lg-8 col-6">
              <h3>{heading}</h3>
            </Col>
            <Col className="col-lg-4 col-6">
              {!IsView && (
                <Row className={s.colActionButton}>
                  {!sequenceVersionManager.selectedSequenceVersion.IsPublished && (
                    <Button
                      style={WidgetVisibility(userProfileWidgets, `SEQUENCEVERSIONPUBLISH`)}
                      variant="primary"
                      data-unittest="saveData"
                      className={`${s.margin5}, ${s.btnSaveChanges}`}
                      noValidate
                      onClick={() => {
                        onPublishBtnClick();
                      }}
                    >
                      <FormattedMessage id="sequenceVersionManagement.publish" defaultMessage="publish" />
                    </Button>
                  )}
                </Row>
              )}
            </Col>
          </Row>
        </div>

        <div className='pb-3'>
          <Breadcrumb items={breadcrumbItems} />
        </div>

        <div className={s.sequenceVersionDetailContent}>
          <Form className={s.viewRow}>
            {formError && formError.PublishError && (
              <p role="alert" className={s.error}>
                {formError.PublishError}
              </p>
            )}

            <Row>
              <Col xs={12} lg={6}>
                <Form.Group controlId="formSequenceVersionName">
                  <Form.Label>
                    <FormattedMessage id="sequenceVersionManagement.name" defaultMessage="Name" />
                  </Form.Label>

                  <Form.Control
                    type="text"
                    name="Name"
                    onChange={onChange}
                    value={sequenceVersionManager.selectedSequenceVersion.Name}
                    className={`${s.formControl} ${formError && formError.Name ? s.formControlError : ''}`}
                    placeholder={intl.formatMessage({
                      id: 'sequenceVersionManagement.name',
                      defaultMessage: 'Name'
                    })}
                    disabled={IsView}
                  />
                  {formError && formError.Name && (
                    <p role="alert" className={s.error}>
                      {formError.Name}
                    </p>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col xs={12} lg={6}>
                <Form.Group controlId="formSequenceVersionNo">
                  <Form.Label>
                    <FormattedMessage id="sequenceVersionManagement.versionNo" defaultMessage="Version Number" />
                  </Form.Label>

                  <Form.Control
                    type="text"
                    name="VersionNo"
                    onChange={onVersionNoChange}
                    value={sequenceVersionManager.selectedSequenceVersion.VersionNo}
                    className={`${s.formControl} ${formError && formError.VersionNo ? s.formControlError : ''}`}
                    placeholder={intl.formatMessage({
                      id: 'sequenceVersionManagement.versionNo',
                      defaultMessage: 'Version Number'
                    })}
                    disabled={IsView}
                  />
                  {formError && formError.VersionNo && (
                    <p role="alert" className={s.error}>
                      {formError.VersionNo}
                    </p>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Group controlId="formSequenceVersionDescription">
                  <Form.Label>
                    <FormattedMessage id="sequenceVersionManagement.description" defaultMessage="Description" />
                  </Form.Label>

                  <Form.Control
                    className={s.txtArea}
                    as="textarea"
                    rows="3"
                    name="Description"
                    onChange={onChange}
                    placeholder={intl.formatMessage({
                      id: 'sequenceVersionManagement.description',
                      defaultMessage: 'Description'
                    })}
                    value={sequenceVersionManager.selectedSequenceVersion.Description}
                    disabled={IsView}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col xs={12} lg={6}>
                <Form.Group controlId="formSequenceVersionVerifyErrorCode">
                  <Form.Label>
                    <FormattedMessage id="sequenceVersionManagement.verifyErrorCode" defaultMessage="Verify Error Code" />
                  </Form.Label>

                  <Form.Control
                    type="text"
                    name="VerifyErrorCode"
                    onChange={onChange}
                    value={sequenceVersionManager.selectedSequenceVersion.VerifyErrorCode}
                    className={`${s.formControl} ${formError && formError.VerifyErrorCode ? s.formControlError : ''}`}
                    placeholder={intl.formatMessage({
                      id: 'sequenceVersionManagement.verifyErrorCode',
                      defaultMessage: 'Verify Error Code'
                    })}
                    disabled={IsView}
                  />
                  {formError && formError.VerifyErrorCode && (
                    <p role="alert" className={s.error}>
                      {formError.VerifyErrorCode}
                    </p>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col xs={12} lg={6}>
                <Form.Group controlId="formSequenceVersionVerifyWarningCode">
                  <Form.Label>
                    <FormattedMessage id="sequenceVersionManagement.verifyWarningCode" defaultMessage="Verify Warning Code" />
                  </Form.Label>

                  <Form.Control
                    type="text"
                    name="VerifyWarningCode"
                    onChange={onChange}
                    value={sequenceVersionManager.selectedSequenceVersion.VerifyWarningCode}
                    className={`${s.formControl} ${formError && formError.VerifyWarningCode ? s.formControlError : ''}`}
                    placeholder={intl.formatMessage({
                      id: 'sequenceVersionManagement.verifyWarningCode',
                      defaultMessage: 'Verify Warning Code'
                    })}
                    disabled={IsView}
                  />
                  {formError && formError.VerifyWarningCode && (
                    <p role="alert" className={s.error}>
                      {formError.VerifyWarningCode}
                    </p>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col lg={6}>
                <Form.Group controlId="formSequenceVersionUnit">
                  <Form.Label>
                    <FormattedMessage id="sequenceVersionManagement.sequenceStartState" defaultMessage="Sequence Start State" />
                  </Form.Label>

                  <Dropdown
                    id="formSequenceVersionSequences"
                    dataArray={sequenceSetpointOptions()}
                    controlData={{ placeholderText: 'Select start state', customClassName: ddlClass }}
                    onDropdownChange={onDropdownChange}
                    selectedOption={sequenceSetpointOptions().filter(
                      option => option.value == sequenceVersionManager.selectedSequenceVersion.StartStateId
                    )}
                    disabled={IsView}
                    data-unittest="formSequenceVersionSequences"
                  />
                  {formError && formError.StartStateId && (
                    <p role="alert" className={s.error}>
                      {formError.StartStateId}
                    </p>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </div>
        <div>
          <SequenceVersionFiles
            onSequenceFileRemove={onSequenceFileRemove}
            addNewBtnClick={addNewbtnClick}
            viewOrChangeBtnClick={viewOrChangeBtnClick}
            selectedFiles={sequenceVersionManager.selectedSequenceVersion.SequenceVersionFiles}
            isView={IsView}
            isEdit={IsEdit}
            isPublished={isPublished}
            sequenceSetpointsOptions={sequenceSetpointOptions}
            currentTimezone={currentTimezone}
            deleteAllowed={deleteAllowed}
            changeAllowed={changeAllowed}
            isModelCategoryOwner={isModelCategoryOwner}
          />
        </div>
        {formError && formError.Task && (
          <p role="alert" className={s.error}>
            {formError.Task}
          </p>
        )}
        <div>
          {!isPublished && isModelCategoryOwner && changeAllowed &&
            <SequenceFileDropzone
              uploadedFiles={sequenceVersionManager.selectedSequenceVersion.SequenceVersionFiles}
              flowSequences={flowSequences}
              updateDetailsFromSequenceConfig={updateDetailsFromSequenceConfig}
              onValidateCallback={selectedFilesValidateCallback}
              onFileUploadStatusChangeCallback={fileUploadStatusCallback}
              fileUploadStatus={sequenceVersionManager.selectedSequenceVersion.fileUploadStatus}
            />}
        </div>
        <div>
          <Form>
            {!IsView && (
              <Button
                variant="primary"
                data-unittest="saveData"
                className={classNames(s.margin5, s.btnSaveChanges)}
                onClick={submitForm}
                noValidate
                disabled={IsView}
              >
                <FormattedMessage id="sequenceVersionManagement.saveChanges" defaultMessage="Save Changes" />
              </Button>
            )}
            <Link to={backLink}>
              <Button variant="outline-secondary" className={s.btnCancel}>
                <FormattedMessage id="sequenceVersionManagement.cancel" defaultMessage="Cancel" />
              </Button>
            </Link>
          </Form>
        </div>
      </div>
    </ModelConfigurationContentTemplate>
  </div>
);
};


SequenceVersionDetail.propTypes = {
  saveSequenceVersion: PropTypes.func.isRequired,
  sequenceVersionManager: PropTypes.object.isRequired,
  describeSequenceVersion: PropTypes.func.isRequired,
  changeSequenceVersion: PropTypes.func.isRequired
};

export default injectIntl(SequenceVersionDetail);
